import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
  useRef,
} from "react";
import openSocket from "../../services/socket-io";
import { Inbox } from "lucide-react";

import TicketListItem from "../TicketListItem";
import usePresence from "../../hooks/usePresence";
import useWhatsApps from "../../hooks/useWhatsApps";
import usePermissions from "../../hooks/usePermissions";
import TicketsListSkeleton from "../TicketsListSkeleton";
import EmptyStateCard from "../EmptyStateCard";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	usuarioPodeVerOuMencao,
	temMencaoAtivaUsuario,
} from "../../helpers/permissaoTicket";

const resumoDaMensagemDoSocket = (mensagem, fallback = "") => {
  if (!mensagem) return fallback;

  const corpo = String(mensagem.body || "").trim();
  if (corpo) return corpo;

  const tipo = String(mensagem.mediaType || "").toLowerCase();

  if (tipo === "location") return "Localização";
  if (tipo === "vcard") return "Contato";
  if (tipo === "sticker") return "Figurinha";
  if (tipo === "image") return "Imagem";
  if (tipo === "video") return "Vídeo";
  if (tipo === "audio" || tipo === "ptt") return "Áudio";
  if (tipo === "document") return "Documento";

  return fallback;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        const antigo = state[ticketIndex];
        // Mesmo merge inteligente dos outros reducers: NÃO apaga as
        // relações (.whatsapp, .contact, .queue, .user etc.) quando o
        // payload novo (paginação / recarregamento da API) vem cru
        // sem includes carregados — preservamos os valores já
        // renderizados antes pro usuário NÃO VER o ícone mudar de canal.
        const fundir = (nomeProp) =>
          ticket[nomeProp] === undefined && antigo?.[nomeProp] !== undefined
            ? { [nomeProp]: antigo[nomeProp] }
            : {};

        state[ticketIndex] = {
          ...antigo,
          ...ticket,
          ...fundir("whatsapp"),
          ...fundir("contact"),
          ...fundir("queue"),
          ...fundir("user"),
          ...fundir("tags"),
          ...fundir("hasMentionForUser"),
          ...fundir("hasUnreadMentionForUser"),
          ...fundir("unreadMentions"),
          // "agendamentoPendente" só existe no que vem da API /tickets (o
          // evento de socket traz o ticket cru, sem esse campo). Preserva o
          // selo "Agendado" caso algum evento não traga o campo.
          ...fundir("agendamentoPendente"),
        };
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex(
      (t) => String(t.id) === String(ticket.id),
    );
    if (ticketIndex !== -1) {
      const antigo = state[ticketIndex];
      const fundir = (nomeProp) =>
        ticket[nomeProp] === undefined && antigo?.[nomeProp] !== undefined
          ? { [nomeProp]: antigo[nomeProp] }
          : {};

      const atualizado = {
        ...antigo,
        ...ticket,
        ...fundir("whatsapp"),
        ...fundir("contact"),
        ...fundir("queue"),
        ...fundir("user"),
        ...fundir("tags"),
        ...fundir("hasMentionForUser"),
        ...fundir("hasUnreadMentionForUser"),
        ...fundir("unreadMentions"),
        // O evento "ticket" do socket não carrega o agendamento pendente
        // (campo calculado pela API /tickets); sem esta fusão um simples
        // evento de atualização apagaria o selo "Agendado" da linha.
        ...fundir("agendamentoPendente"),
      };

      const ultimoMsgMudou =
        ticket.lastMessage !== undefined &&
        String(antigo.lastMessage) !== String(ticket.lastMessage);
      const updatedAtMudou =
        ticket.updatedAt !== undefined &&
        new Date(ticket.updatedAt).getTime() !==
          new Date(antigo.updatedAt || 0).getTime();
      const mudouConteudo = ultimoMsgMudou || updatedAtMudou;

      state[ticketIndex] = { ...atualizado, lastMessage: atualizado.lastMessage };

      if (mudouConteudo && ticketIndex > 0) {
        const [linha] = state.splice(ticketIndex, 1);
        state.unshift(linha);
      }

      if (ultimoMsgMudou) return [...state];
    } else {
      // ⭐ BARREIRA ANTI-FUGA (UPDATE_TICKET else): só insere UM TICKET
      //     NOVO na lista SE realmente ele tem permissão de aparecer aqui.
      //
      // Antes: sempre `state.unshift(ticket)`. O bug real que isto causava:
      //   1. Atendente transfere ticket para colega.
      //   2. socket.onTicket executa: DELETE_TICKET → remove a linha.
      //   3. Imediatamente DEPOIS (ou evento separado "ticket" com payload
      //      diferente — ex: mudou lastMessage/updatedAt pelo dono novo)
      //      chega outro UPDATE_TICKET — ticketIndex===-1 (pois acabou de
      //      sair!) → unshift de volta. Resultado VISUAL para o usuário:
      //      "transferi mas continua aparecendo pra mim".
      //
      // Aqui no reducer não temos props.user / showAll / visao (são props
      // do componente). Então a barreira fechamos em DOIS níveis:
      //
      //   [NÍVEL 1] — AQUI no reducer, pelo que o action.payload traz:
      //      se o dispatcher (onTicket no useEffect) passar currentUserId,
      //      showAll e visao junto — aplicamos a lógica e bloqueamos óbvios.
      //
      //   [NÍVEL 2] — NO onTicket (useEffect) existe uma barreira + PURGE
      //      genérico com shouldUpdateTicket completo que remove tudo que
      //      ainda sobrar após os dispatches.
      const userIdDono = Number(ticket?.userId || 0);
      const userIdEu =
        action && "currentUserId" in action ? Number(action.currentUserId || 0) : 0;
      const showAllEvento =
        action && "showAll" in action
          ? action.showAll === true || action.showAll === "true"
          : null;
      const visaoEvento = action && "visao" in action ? String(action.visao || "") : null;

      // Se o evento informou contexto — bloqueia caso óbvio: dono é outra
      // pessoa e visão não libera. Se não informou contexto → permitimos
      // inserir e deixamos o PURGE no useEffect remover depois (melhor
      // remover duas vezes do que não inserir um ticket que deve ficar).
      const informouContexto = userIdEu !== 0;
      let naoDeviaInserir = false;
      if (informouContexto && userIdDono !== 0 && userIdDono !== userIdEu) {
        const showAllLibera = showAllEvento === true;
        const visaoSetor = visaoEvento === "setor" || visaoEvento === null;
        const temMencao = Boolean(ticket?.hasUnreadMentionForUser || ticket?.hasMentionForUser);
        if (!showAllLibera && !visaoSetor && !temMencao) {
          naoDeviaInserir = true;
        }
      }

      if (!naoDeviaInserir) {
        state.unshift(ticket);
      }
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const { ticket, message } = action.payload;
    const resumoNovo = resumoDaMensagemDoSocket(message, ticket?.lastMessage);
    const updatedAtNovo =
      message?.createdAt || message?.updatedAt || ticket?.updatedAt;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Mesma fusão inteligente de UPDATE_TICKET: quando o payload novo
      // não tem as relações (whatsapp/contact etc.) NÃO apaga as antigas.
      const antigo = state[ticketIndex];
      const fundir = (nomeProp) =>
        ticket[nomeProp] === undefined && antigo?.[nomeProp] !== undefined
          ? { [nomeProp]: antigo[nomeProp] }
          : {};

      state[ticketIndex] = {
        ...antigo,
        ...ticket,
        ...fundir("whatsapp"),
        ...fundir("contact"),
        ...fundir("queue"),
        ...fundir("user"),
        ...fundir("tags"),
        ...fundir("hasMentionForUser"),
        ...fundir("hasUnreadMentionForUser"),
        ...fundir("unreadMentions"),
        // Idem UPDATE_TICKET: mensagem nova não pode apagar o selo.
        ...fundir("agendamentoPendente"),
        ...(resumoNovo ? { lastMessage: resumoNovo } : {}),
        ...(updatedAtNovo ? { updatedAt: updatedAtNovo } : {}),
      };
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift({
        ...ticket,
        ...(resumoNovo ? { lastMessage: resumoNovo } : {}),
        ...(updatedAtNovo ? { updatedAt: updatedAtNovo } : {}),
      });
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_LAST_MESSAGE_FROM_MESSAGE") {
    const { ticketId, mensagemAtualizada } = action.payload;
    if (!ticketId || !mensagemAtualizada) return state;
    const idx = state.findIndex((t) => String(t.id) === String(ticketId));
    if (idx === -1) return state;
    const ticketLocal = state[idx];
    const deletada =
      mensagemAtualizada.isDeleted === true ||
      String(mensagemAtualizada.isDeleted) === "true";
    const editada =
      mensagemAtualizada.editedAt !== undefined &&
      mensagemAtualizada.editedAt !== null;
    const temBody = typeof mensagemAtualizada.body === "string";
    let novoLast = ticketLocal.lastMessage;
    let mudou = false;
    if (deletada) {
      novoLast = "Esta mensagem foi apagada";
      mudou = true;
    } else if (editada && temBody) {
      novoLast = mensagemAtualizada.body;
      mudou = true;
    } else if (temBody && !ticketLocal.lastMessage) {
      novoLast = mensagemAtualizada.body;
      mudou = true;
    }
    if (!mudou) return state;
    const atualizado = {
      ...ticketLocal,
      lastMessage: novoLast,
      updatedAt: new Date().toISOString(),
    };
    const novoState = [...state];
    novoState[idx] = atualizado;
    return [...novoState];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsList = (props) => {
  const {
    status,
    searchParam,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    isGroup,
    vazio,
    ordemAZ,
    tagIds,
    userIds,
    whatsappIds,
    // props.whatsApps vem opcionalmente do TicketsManager (para manter o mesmo
    // estado de conexoes ja carregado la). Se nao vier, fallback para o
    // hook useWhatsApps aqui mesmo — usado no antigo comportamento e na
    // busca (TicketsManager passa undefined/nao passa nada no search).
    whatsApps: whatsAppsFromProps,
    departmentId,
    dataInicio,
    dataFim,
    visao,
    onlyFavorites,
    ultimosDias,
    agruparPorContato,
  } = props;
  const whatsAppsFallback = useWhatsApps();
  const whatsApps = Array.isArray(whatsAppsFromProps)
    ? whatsAppsFromProps
    : whatsAppsFallback?.whatsApps || [];
  const loadingWhatsapps = Array.isArray(whatsAppsFromProps)
    ? false
    : Boolean(whatsAppsFallback?.loading);
  // Mapa slaCountGroups por id de conexao — usado em TicketListItem para
  // saber quando um ticket eh de grupo e a conexao nao tem SLA de grupos
  // ligado. Nesses casos (apenas NA ABA GRUPOS), os botoes
  // Aceitar / Transferir / Ignorar NAO sao exibidos.
  const slaCountGroupsMap = useMemo(() => {
    const map = new Map();
    for (const w of Array.isArray(whatsApps) ? whatsApps : []) {
      if (w && (w.id !== undefined && w.id !== null)) {
        map.set(Number(w.id), Boolean(w.slaCountGroups));
      }
    }
    return map;
  }, [whatsApps]);
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  // Muda quando algum agendamento é criado/editado/apagado (evento
  // "scheduledMessage" do socket). Serve só para forçar a releitura da
  // lista — é assim que o selo "Agendado" aparece/some sem F5.
  const [agendamentosVersao, setAgendamentosVersao] = useState(0);
  const { user } = useContext(AuthContext);
  // Recorte de tickets do cargo. Só quem enxerga a empresa inteira pode ver
  // grupo fora das próprias filas — ver o comentário na conferência de fila.
  const { ticketScope } = usePermissions();

  // A aba pode pedir mais de uma situação ("open,pending" na de Grupos:
  // ninguém "aceita" uma conversa de grupo, então ela vive nas duas). Cada
  // sala do Socket.IO é de UMA situação — entrar numa sala chamada
  // "open,pending" é entrar numa sala para onde o servidor nunca emite, e a
  // aba só se mexia com F5.
  const situacoes = useMemo(
    () =>
      String(status || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [status],
  );

  // Na aba Grupos (isGroup === "true") a lista de status pede "closed" para
  // trazer os grupos que são APENAS "histórico automático" (aqueles gravados
  // com groupAsTicket=false no handleWhatsappEvents, sem atendimento). Esses
  // grupos tem closeReason NULL. Quando o usuário CLICA EM IGNORAR ou
  // RESOLVER um grupo de propósito, o UpdateTicketService grava
  // closeReason != null ("Ignorado" ou o motivo do modal) — esses grupos
  // precisam SAIR da aba Grupos e ir para a aba Fechados igual tickets
  // individuais. Sem este filtro no FRONTEND: (1) os tickets já carregados
  // na carga inicial continuam aparecendo até F5, e (2) o evento de socket
  // action=update NÃO removia a linha pq matchesStatus ainda dava true
  // (closed ainda está na lista). Implementamos a mesma regra do
  // ListTicketsService aqui no cliente para manter tempo real consistente.
  const deveRemoverGrupoEncerrado = (ticket) =>
    !!ticket &&
    isGroup === "true" &&
    situacoes.includes("closed") &&
    ticket.status === "closed" &&
    Boolean(ticket.closeReason);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [
    situacoes,
    searchParam,
    dispatch,
    showAll,
    selectedQueueIds,
    isGroup,
    visao,
    onlyFavorites,
  ]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    departmentId,
    dataInicio,
    dataFim,
    visao,
    onlyFavorites,
    ultimosDias,
    agruparPorContato,
    // Refaz a leitura da lista (e o selo "Agendado") quando um agendamento
    // muda em qualquer lugar do sistema.
    refreshKey: agendamentosVersao,
  });

  useEffect(() => {
    if (!status && !searchParam) return;
    const situacoesArr = String(status || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const ticketsComStatusValido = (tickets || []).filter((t) => {
      if (situacoesArr.length && !situacoesArr.includes(t?.status)) return false;
      if (deveRemoverGrupoEncerrado(t)) return false;
      return true;
    });
    dispatch({
      type: "LOAD_TICKETS",
      payload: ticketsComStatusValido,
    });
  }, [tickets, status, searchParam, isGroup]);

  // O que já está na tela. O evento do socket carrega o ticket "cru", igual
  // para todo mundo: ele não diz se é favorito de quem está olhando nem de
  // quem é o recorte da aba. Guardar os ids visíveis é o que permite decidir
  // isso sem uma ida ao servidor por evento.
  const idsVisiveis = useRef([]);

  // ticketsList atualizado em tempo real pro listener do socket poder checar
  // se um ticket mencionado (vindo do backend por socket) já está na lista
  // ou precisa entrar como novo.
  const ticketsListRef = useRef([]);

  useEffect(() => {
    idsVisiveis.current = ticketsList.map((t) => t.id);
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);

  useEffect(() => {
    const socket = openSocket();

    // O filtro de grupo vale também para o tempo real: sem isso um ticket de
    // grupo apareceria na aba de conversas individuais (e vice-versa).
    const matchesGroupFilter = (ticket) =>
      !ticket || isGroup === undefined || Boolean(ticket.isGroup) === (isGroup === "true");

    // Os filtros da barra valem também para o que chega em tempo real. Sem
    // isto, um ticket novo entrava na lista mesmo sem a etiqueta (ou a
    // conexão, ou o atendente) filtrada: a pessoa filtrava por uma etiqueta e
    // via aparecer conversa que não tinha nada a ver com ela.
    const matchesTagFilter = (ticket) =>
      !ticket || !tagIds?.length ||
      (ticket.tags || []).some((tag) => tagIds.includes(tag.id));

    const matchesWhatsappFilter = (ticket) =>
      !ticket || !whatsappIds?.length || whatsappIds.includes(ticket.whatsappId);

    const matchesUserFilter = (ticket) =>
      !ticket || !userIds?.length || userIds.includes(ticket.userId);

    // Cada lista é de um status só. A conexão do Socket.IO é uma para o app
    // inteiro, então TODA lista recebe o evento de TODO ticket — sem esta
    // conferência, uma mensagem nova num atendimento já aceito reinseria ele
    // na aba "Aguardando", e o mesmo cliente aparecia nas duas ao mesmo tempo.
    const matchesStatus = (ticket) =>
      !ticket ||
      !situacoes.length ||
      situacoes.includes(ticket.status);

    // A aba Grupos não filtra por fila para quem enxerga a empresa inteira: o
    // grupo com fila fixa sumia para quem não tinha aquela fila. É a mesma
    // regra do ListTicketsService — se as duas divergirem, a linha aparece no
    // F5 e não em tempo real (ou o contrário).
    const ignoraFila = isGroup === "true" && ticketScope === "company";

    // Grupo é conversa da equipe: quem já enxerga além do próprio nome vê o
    // grupo assumido pelo colega.
    const grupoSemDono = isGroup === "true" && ticketScope !== "own";

    const matchesQueueFilter = (ticket) =>
      !ticket ||
      ignoraFila ||
      !ticket.queueId ||
      selectedQueueIds.indexOf(ticket.queueId) > -1;

    // Eixo dono/setor: "Minhas" só aceita o que está na mão de quem entrou;
    // "Do setor" aceita conversa de colega — é justamente o que ela mostra.
    const matchesVisao = (ticket) =>
      !ticket || visao !== "minhas" || ticket.userId === user?.id;

    // Sem saber se é favorito, o critério seguro é "já estava na lista":
    // conversa recém-favoritada entra na próxima carga, e nenhuma
    // não-favorita se infiltra pelo tempo real.
    const matchesFavorito = (ticket) =>
      !ticket || !onlyFavorites || idsVisiveis.current.includes(ticket.id);

    const shouldUpdateTicket = (ticket) => {
      if (!ticket || searchParam) return false;

      // ----------------------------------------------------------
      // CORREÇÃO FUGA DE TICKETS DE OUTROS ATENDENTES
      // ----------------------------------------------------------
      // O backend emite eventos "ticket" para TODOS da empresa (sala status).
      // O primeiro load HTTP /tickets já vem filtrado por userId/perfil, mas
      // no socket um atendente "user" comum não pode receber/inserir na sua
      // lista tickets atribuídos a OUTRO colega. Aqui bloqueamos o update
      // ANTES de qualquer outra checagem.
      if (!usuarioPodeVerOuMencao(user, ticket)) return false;

      // PRIMEIRO filtra por status (situacao da aba). Se o ticket aberto
      // não pertence ao status desta aba → NÃO inserir atualizar aqui,
      // senão aparece "Aguardando" dentro da aba Atendimento.
      if (!matchesStatus(ticket)) return false;
      if (!matchesFavorito(ticket)) return false;
      if (!matchesGroupFilter(ticket)) return false;
      if (!matchesTagFilter(ticket)) return false;
      if (!matchesWhatsappFilter(ticket)) return false;
      if (!matchesUserFilter(ticket)) return false;

      // CASO ESPECIAL (menção ativa para MIM): se eu fui mencionado e ainda
      // não dispenso, a linha PASSA mesmo que: eu não seja dono, não seja
      // minha fila, não seja visao minhas etc. — SEMPRE entra. Ao dispensar,
      // o backend atualiza as flags para false e shouldUpdateTicket deixa
      // passar só se encaixar nas regras padrão abaixo (ou some).
      if (temMencaoAtivaParaMim(ticket)) return true;

      // Regras padrão para quem não é menção ativa.
      if (!matchesVisao(ticket)) return false;

      // Quem enxerga a empresa toda (admin/supervisor, scope "company") vê
      // TUDO que encaixar no status, período e filtros da aba. Não precisa
      // ligar "mostrar todos" (embora venha ligado por padrão), não precisa
      // ser dono, não precisa estar em aberto sem dono. A checagem abaixo
      // só aplica a atendentes comuns (escopos own/team/queues).
      if (ticketScope === "company") {
        if (!matchesQueueFilter(ticket)) return false;
        return true;
      }

      if (
        !(
          grupoSemDono ||
          visao === "setor" ||
          !ticket.userId ||
          ticket.userId === user?.id ||
          showAll
        )
      ) {
        return false;
      }
      if (!matchesQueueFilter(ticket)) return false;
      return true;
    };

    const notBelongsToUserQueues = (ticket) =>
      !!ticket && !matchesQueueFilter(ticket);

    // Ticket tem menção ATIVA para este usuário (ainda não dispensou
    // explicitamente clicando em Aceitar ou Fechar). Quando retorna true,
    // NÃO removemos o ticket da lista mesmo que: ele não seja dono, não
    // pertença às filas dele, não seja da visão etc. — até ele dispensar.
    const temMencaoAtivaParaMim = (ticket) => temMencaoAtivaUsuario(ticket);

    // Join duplo: (1) IMEDIATO se socket já connected (conexão singleton já aberta
    // por outra tela — evento "connect" NÃO dispara de novo) e (2) no callback
    // "connect" caso a conexão caia e reconecte.
    const entrarNasSalas = () => {
      if (situacoes.length) {
        situacoes.forEach((situacao) => socket.emit("joinTickets", situacao));
      } else {
        socket.emit("joinNotification");
      }
    };
    try { if (socket.connected) entrarNasSalas(); } catch (_) {}
    socket.on("connect", entrarNasSalas);

    const onTicket = (data) => {
      if (!data || !data.action) return;

      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      // Cada lista mostra um status. Quando o ticket muda de status ele
      // tem de sair desta lista e entrar na outra — antes ficava nas duas,
      // e os contadores das abas paravam de bater.
      //
      // REGRA EXTRA PARA ABA GRUPOS (deveRemoverGrupoEncerrado):
      // Na aba Grupos o filtro vem com status "closed" para trazer os
      // grupos que são só "histórico automático" (closeReason=null).
      // Quando usuário clica em Ignorar/Resolver o grupo passa a ter
      // closeReason não nulo. Mesmo que "closed" ainda esteja no filtro,
      // a linha tem que SAIR da aba Grupos e parar de aparecer "Reabrir"
      // lá. Ação é a mesma de um DELETE_TICKET.
      const t = data.ticket;

      // ----------------------------------------------------------
      // CORREÇÃO EXTRA: se evento socket trouxer ticket de outro
      // atendente para um perfil "user" comum, removemos da lista
      // local (se já estava) e ignoramos o dispatch (nunca deixa
      // "aparecer e sumir no F5" — some NA HORA).
      // ----------------------------------------------------------
      if (
        t &&
        data.action !== "delete" &&
        !usuarioPodeVerOuMencao(user, t)
      ) {
        const jaExiste = ticketsListRef.current.some(
          (row) => Number(row.id) === Number(t.id)
        );
        if (jaExiste) {
          dispatch({ type: "DELETE_TICKET", payload: t.id });
        }
        return;
      }

      if (
        data.action === "update" &&
        t &&
        situacoes.length &&
        !situacoes.includes(t.status)
      ) {
        dispatch({ type: "DELETE_TICKET", payload: t.id });
      } else if (
        data.action === "update" &&
        deveRemoverGrupoEncerrado(t)
      ) {
        dispatch({ type: "DELETE_TICKET", payload: t.id });
      } else if (data.action === "update" && shouldUpdateTicket(t)) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: t,
          currentUserId: user?.id ?? null,
          showAll: !!showAll,
          visao: visao ?? null,
        });
      }

      // Na aba "Minhas", perder o dono (transferência, ou um colega que
      // puxou a conversa pela aba do setor) tem de tirar a linha da tela na
      // hora — senão ela continua ali dando a impressão de que o trabalho
      // ainda é meu. EXCEÇÃO: se houver menção ATIVA para mim neste ticket,
      // ele fica na lista mesmo sem ser dono — até eu dispensar (Fechar /
      // Aceitar).
      if (
        data.action === "update" &&
        t &&
        visao === "minhas" &&
        t.userId !== user?.id &&
        !temMencaoAtivaParaMim(t)
      ) {
        dispatch({ type: "DELETE_TICKET", payload: t.id });
      }

      // Na aba "Setor" QUANDO O USUÁRIO DESMARCOU "mostrar todos" (showAll
      // = false), só temos de enxergar: o que é meu + o que ainda está sem
      // dono. Todo ticket recém-transferido para um colega (ticket.userId
      // não é meu e não NULL) tem de SAIR da lista NA HORA, para não dar a
      // impressão de que ainda está sob minha responsabilidade. Esta regra
      // existe para preencher o gap que antes só resolvia no F5 (quando o
      // backend re-roda a consulta com showAll=false).
      if (
        data.action === "update" &&
        t &&
        visao === "setor" &&
        !showAll &&
        t.userId &&
        t.userId !== user?.id &&
        !temMencaoAtivaParaMim(t)
      ) {
        dispatch({ type: "DELETE_TICKET", payload: t.id });
      }

      if (
        data.action === "update" &&
        notBelongsToUserQueues(t) &&
        !temMencaoAtivaParaMim(t)
      ) {
        dispatch({ type: "DELETE_TICKET", payload: t.id });
      }

      if (data.action === "delete" && data.motivo !== "mention") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }

      // ⭐⭐⭐ PURGE GENÉRICO FINAL — a medida que remove qualquer fuga remanescente.
      //
      // Depois de todos os UPDATES e DELETES específicos deste evento socket, varremos
      // a lista atual (ticketsListRef.current) e apagando TUDO que NÃO passa no
      // shouldUpdateTicket. Imediatamente. Por que isto é necessário?
      //
      //   (a) Ordem de eventos na prática é imprevisível: DELETE_TICKET + UPDATE_TICKET
      //       podem ocorrem em ordens diferentes dependendo se o UpdateTicketService
      //       emitiu ticket:ticket ou não; se o FlowJob emitiu 2 vezes. Um UPDATE_TICKET com
      //       ticketIndex=-1 (após DELETE que removeu a linha) reinsere a linha
      //       de volta.
      //
      //   (b) Outros dispatches: UPDATE_TICKET_UNREAD_MESSAGES também tem o mesmo
      //       else unshift — e aquele não tem regras, ele coloca tudo sem checar.
      //
      //   (c) Filtros de usuário/fila/mencao mudam fora do evento (ex: menção foi
      //       dispensada em outro cliente e não veio evento ainda, então já quebrou uma
      //       condição para ficar).
      //
      //   (d) Evento inicial com ticket.user === usuário já não pertencer ao usuário é apagado neste
      //       laço. Por isso esta barreira é obrigatória — fecha todos os gaps de
      //       uma vez, independente do caminho que inseriu a linha no state.
      //
      // É idempotente: chamar muitas vezes não duplica remove. E não afeta o array
      // de 0% de tickets válidos (shouldUpdateTicket retorna true para
      // eles, logo não apaga).
      try {
        if (searchParam) {
          // Se a pessoa está com busca ativa (search) ativada, a ordem/filtragem é
          // feita no backend via HTTP, então não apagamos nada no cliente — senão
          // o resultado do search seria parcialmente apagado.
        } else {
          const idsApagar = [];
          const listaAtual = Array.isArray(ticketsListRef.current || []) ? ticketsListRef.current : [];
          for (let i = 0; i < listaAtual.length; i++) {
            const linha = listaAtual[i];
            if (!linha) continue;
            // Usar shouldUpdateTicket — é O MESMO criterio usado para
            // ACEITAR UM UPDATE nesta aba. Se falhar aqui → nao fica.
            if (!shouldUpdateTicket(linha)) {
              idsApagar.push(Number(linha.id));
            }
          }
          // Despachar deletions em lote (evita re-render por item).
          if (idsApagar.length) {
            idsApagar.forEach((id) => {
              if (typeof id === "number" && !Number.isNaN(id) && id > 0) {
                dispatch({ type: "DELETE_TICKET", payload: id });
              }
            });
          }
        }
      } catch (_) {
        // Nunca quebrar o listener de socket por causa de purge — engolir.
      }
    };
    socket.on("ticket", onTicket);

    // Ticket mencionado via Nota Interna (Mensagem Privada): o backend emite
    // "ticket:mention" pro usuário mencionado. Atualizamos/inserimos na lista
    // NA HORA, sem precisar de F5. Redundante com o emit "ticket: update"
    // que o backend também já manda, mas aqui garantimos que o badge do
    // unreadMentions e a linha entram sempre (mesmo que haja atraso no outro).
    const onTicketMention = (data) => {
      if (!data) return;
      const ticketId = +(data.ticketId || data.mention?.ticketId || 0);
      if (!ticketId) return;

      const idx = ticketsListRef.current.findIndex((t) => +t.id === ticketId);
      if (idx !== -1) {
        // Já existe: atualiza o unreadMentions e garante que mostra o selo.
        const novo = { ...ticketsListRef.current[idx] };
        novo.unreadMentions = Math.max(1, +(novo.unreadMentions || 0) + 1);
        novo.hasUnreadMentionForUser = true;
        dispatch({ type: "UPDATE_TICKET", payload: novo });
      }
      // Caso contrário: o backend JÁ EMITE em seguida "ticket" action "update"
      // com o ticket completo (via ListTicketsService) — o reducer UPDATE_TICKET
      // já tem lógica de "unshift" (insere no começo) se o ticket não existe.
    };
    socket.on("ticket:mention", onTicketMention);

    const onAppMessage = (data) => {
      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: {
            ticket: data.ticket,
            message: data.message,
          },
        });
      }

      if (data.action === "update" && data.message) {
        const ticketId = Number(data.message.ticketId);
        if (!ticketId || Number.isNaN(ticketId)) return;
        const novaMensagem = data.message;

        dispatch({
          type: "UPDATE_TICKET_LAST_MESSAGE_FROM_MESSAGE",
          payload: { ticketId, mensagemAtualizada: novaMensagem },
        });
      }
    };
    socket.on("appMessage", onAppMessage);

    const onContact = (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    };
    socket.on("contact", onContact);

    // Alguém criou, editou ou apagou um agendamento (pela tela de
    // Agendamentos ou de dentro de outro atendimento). O backend só avisa
    // "mudou" e o que mudou; quem define o selo é o campo calculado
    // "agendamentoPendente" que só a API devolve — por isso relemos a lista.
    const onScheduledMessage = () => {
      setAgendamentosVersao((n) => n + 1);
    };
    socket.on("scheduledMessage", onScheduledMessage);

    return () => {
      // NÃO chamar socket.disconnect() — conexão SINGLETON compartilhada por
      // todo o sistema (visto recebido/visto, transferências, chats, presença
      // etc.). Cada mount/unmount da TicketsList (trocar abas, filtros etc.)
      // antes MATAVA a conexão de tudo → todos updates paravam até F5.
      try { socket.off("connect", entrarNasSalas); } catch (_) {}
      try { socket.off("ticket", onTicket); } catch (_) {}
      try { socket.off("ticket:mention", onTicketMention); } catch (_) {}
      try { socket.off("appMessage", onAppMessage); } catch (_) {}
      try { socket.off("contact", onContact); } catch (_) {}
      try { socket.off("scheduledMessage", onScheduledMessage); } catch (_) {}
    };
    // Os filtros entram nas dependências: sem eles os ouvintes seguiriam
    // enxergando a seleção de quando foram registrados, e voltariam a deixar
    // passar ticket que o filtro atual exclui.
  }, [
    situacoes,
    searchParam,
    showAll,
    user,
    ticketScope,
    selectedQueueIds,
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    visao,
    onlyFavorites
  ]);

  // Filtro novo = lista nova: sem zerar, os resultados antigos ficariam
  // misturados com os do filtro.
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [tagIds, userIds, whatsappIds, departmentId, dataInicio, dataFim, selectedQueueIds, showAll, visao]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  // Ordem alfabética é uma visão do que já foi carregado; a ordem padrão
  // (mais recente primeiro) continua vindo do servidor.
  const listaVisivel = useMemo(() => {
    if (!ordemAZ) return ticketsList;

    return [...ticketsList].sort((a, b) =>
      (a.contact?.name || "").localeCompare(b.contact?.name || "", "pt-BR"),
    );
  }, [ticketsList, ordemAZ]);

  // Um socket de presença para a lista inteira, não um por item.
  const presencas = usePresence();

  // A conexão só aparece no item quando a empresa tem mais de uma — com
  // uma só, o nome se repetiria em todas as linhas sem informar nada.
  // A lista whatsApps ja foi carregada mais acima (via props ou via
  // useWhatsApps), portanto nao precisamos de outro hook aqui — reutiliza.
  const mostrarConexao = (whatsApps || []).length > 1;

  // O tempo de espera precisa envelhecer na tela; sem isto "2 min" ficaria
  // congelado até a próxima mensagem.
  const [, redesenhar] = useState(0);

  useEffect(() => {
    const t = setInterval(() => redesenhar((n) => n + 1), 30000);

    return () => clearInterval(t);
  }, []);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      e.currentTarget.scrollTop = scrollTop - 100;
      loadMore();
    }
  };

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      style={style}
    >
      <div
        className="scrollbar-discreta flex-1 overflow-y-auto"
        onScroll={handleScroll}
      >
        {ticketsList.length === 0 && !loading ? (
          <EmptyStateCard
            variant="flex"
            size="md"
            intent="primary"
            icon={Inbox}
            title={vazio?.titulo || i18n.t("ticketsList.noTicketsTitle")}
            subtitle={vazio?.texto || i18n.t("ticketsList.noTicketsMessage")}
            action={vazio?.acao || null}
          />
        ) : (
          listaVisivel.map((ticket) => (
            <TicketListItem
              ticket={ticket}
              key={ticket.id}
              presenca={presencas[ticket.id]}
              mostrarConexao={mostrarConexao}
              isGroup={isGroup}
              slaCountGroupsMap={slaCountGroupsMap}
              loadingWhatsapps={loadingWhatsapps}
            />
          ))
        )}
        {loading && <TicketsListSkeleton />}
      </div>
    </div>
  );
};

export default TicketsList;
